import { useEffect, useState } from "react";

import { FileImageErrored } from "@/components/file-image-errored/FileImageErrored";
import {
  CommonGenerationTileProps,
  GenerationTileAction,
} from "@/components/Tiles/GenerationTile/types";
import {
  TileBase,
  TileLikeButton,
  TileObjectTypeIndicator,
} from "@/components/ui-v2";
import { SessionFileFragment } from "@/features/forge/forge-session/queries.graphql";
import { getSrcSet, normalizeThumbnails } from "@/utils/thumbnails";

import { ImageGenerationTileButtons } from "./ImageGenerationTileButtons";

export const ImageGenerationTile = (
  props: CommonGenerationTileProps<SessionFileFragment>,
) => {
  const {
    object,
    isSelected = false,
    isSelectable = false,
    isLiking = false,
    onAction,
    ...rest
  } = props;
  const [isImageError, setIsImageError] = useState(false);

  const { thumbnails, previewUrl, isLiked, metadata: fileMetadata } = object;

  const maxWidth = fileMetadata?.width ?? 1024;
  const maxHeight = fileMetadata?.height ?? 1024;

  const srcSet =
    object.inferenceParameters?.vectorize || !previewUrl
      ? undefined
      : getSrcSet({
          thumbnails: normalizeThumbnails(thumbnails),
          metadata: fileMetadata
            ? { width: fileMetadata.width ?? undefined }
            : undefined,
          url: previewUrl,
        });

  const handleTileBaseAction =
    (action: GenerationTileAction) => (e: React.MouseEvent) => {
      onAction?.(e, action);
    };

  const handleImageError = () => {
    setIsImageError(true);
  };

  useEffect(() => {
    setIsImageError(false);
  }, [previewUrl]);

  return (
    <TileBase
      isSelected={isSelected}
      isSelectable={isSelectable}
      slots={{
        bottomLeftEl: ImageGenerationTileButtons,
        topLeftEl: TileObjectTypeIndicator,
        bottomRightEl: TileLikeButton,
      }}
      slotProps={{
        bottomLeftEl: {
          onAction,
        },
        topLeftEl: {
          isSelected,
          objectType: "image",
        },
        bottomRightEl: {
          isLiked,
          isLoading: isLiking,
          onAction,
          sx: {
            m: 1,
          },
        },
      }}
      onClick={handleTileBaseAction("SELECT")}
      onDoubleClick={handleTileBaseAction("DOUBLE_CLICK")}
      onContextMenu={handleTileBaseAction("OPEN_CONTEXT_MENU")}
      {...rest}
    >
      {isImageError || !previewUrl ? (
        <FileImageErrored
          dismissable={false}
          errorMessage="Preview image could not be loaded."
        />
      ) : (
        <img
          src={previewUrl}
          srcSet={srcSet}
          sizes="auto"
          loading="lazy"
          onError={handleImageError}
          alt={object.name}
          draggable={false}
          style={{
            maxWidth,
            maxHeight,
          }}
        />
      )}
    </TileBase>
  );
};
